/**
 * MapKit JS bootstrap + token management.
 *
 * mapkit-react only loads the MapKit JS bundle and calls `mapkit.init`
 * when a `<Map>` component mounts. That's not enough for our use cases —
 * the LocationPicker uses `mapkit.Search` and `mapkit.Geocoder` even
 * before the toggle is on, and `MapKitAutocompleteField` (the City /
 * State / Country autocompletes) doesn't render a Map at all.
 *
 * This module mirrors mapkit-react's loader pattern (script-tag injection
 * + `mapkit.init` with an authorizationCallback that hands the SDK a
 * freshly-minted JWT) but exposes it for direct use. Idempotent: calling
 * `ensureMapKitJsLoaded()` multiple times only loads / inits once.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type {
  GetMapKitTokenInput,
  GetMapKitTokenResponse,
} from '@event-planner/shared';

const getMapKitTokenCallable = httpsCallable<
  GetMapKitTokenInput,
  GetMapKitTokenResponse
>(functions, 'getMapKitToken');

let cachedToken: string | null = null;
let cachedTokenExpiry = 0;

export async function fetchMapKitToken(): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  if (cachedToken && cachedTokenExpiry > now + 60) {
    return cachedToken;
  }
  const origin = window.location.origin;
  const result = await getMapKitTokenCallable({ origin });
  cachedToken = result.data.token;
  cachedTokenExpiry = result.data.expiresAt;
  return cachedToken;
}

// ---------------------------------------------------------------------------
// MapKit JS bootstrap
// ---------------------------------------------------------------------------

const MAPKIT_JS_SRC = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';

let loadPromise: Promise<void> | null = null;
let initialized = false;

function callInit(): void {
  if (initialized) return;
  initialized = true;
  // Per Apple's docs the authorizationCallback may be invoked multiple
  // times during the page's lifetime (when the JWT expires). The
  // fetcher caches and refreshes so each call returns a fresh token
  // without an extra round-trip until expiry.
  mapkit.init({
    authorizationCallback: async (done: (token: string) => void) => {
      try {
        const token = await fetchMapKitToken();
        done(token);
      } catch {
        done('');
      }
    },
  });
}

/**
 * Ensure MapKit JS is loaded and initialized. Resolves once `mapkit` is
 * usable (Search / Geocoder / etc.). Safe to call from multiple
 * components simultaneously — only one script tag is injected and only
 * one init runs.
 */
export function ensureMapKitJsLoaded(): Promise<void> {
  if (loadPromise) return loadPromise;

  // Already loaded by someone else (e.g. mapkit-react's Map component).
  if (typeof mapkit !== 'undefined') {
    callInit();
    loadPromise = Promise.resolve();
    return loadPromise;
  }

  // Someone (mapkit-react's loader) has the script tag in flight but
  // mapkit isn't on `window` yet. Re-use that script's load event.
  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[src*="mapkit.js"]`,
  );
  if (existingScript) {
    loadPromise = new Promise<void>((resolve) => {
      existingScript.addEventListener(
        'load',
        () => {
          callInit();
          resolve();
        },
        { once: true },
      );
      // Edge case: script may have already finished loading between our
      // querySelector and addEventListener. Guard with a deferred check.
      queueMicrotask(() => {
        if (typeof mapkit !== 'undefined') {
          callInit();
          resolve();
        }
      });
    });
    return loadPromise;
  }

  // First mount on this page — inject the script ourselves.
  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = MAPKIT_JS_SRC;
    script.crossOrigin = 'anonymous';
    script.addEventListener(
      'load',
      () => {
        callInit();
        resolve();
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => reject(new Error('Failed to load MapKit JS')),
      { once: true },
    );
    document.head.appendChild(script);
  });
  return loadPromise;
}
